import { type ChainFullId, ChainFullIds } from "@umami/chains";
import { type UnifiedBalanceWithPrices } from "@umami/state";

interface TokenListToken {
  address: string;
  chainId: number;
  decimals: number;
  logoURI: string;
  name: string;
  symbol: string;
  tags?: string[];
}

interface TokenListResponse {
  [address: string]: TokenListToken;
}

export interface TokenWithAddress extends UnifiedBalanceWithPrices {
  address?: string;
  logoURI?: string;
}

const tokenCache = new Map<string, TokenWithAddress[]>();

export const fetchPopularTokens = async (chainId: ChainFullId): Promise<TokenWithAddress[]> => {
  const cacheKey = `popular-${chainId}`;

  if (tokenCache.has(cacheKey)) {
    return tokenCache.get(cacheKey)!;
  }

  try {
    const chainIdMap: Record<string, string> = {
      [ChainFullIds.ETHEREUM]: "1",
    };

    const inchChainId = chainIdMap[chainId];

    if (!inchChainId) {
      if (chainId === ChainFullIds.ETHERLINK) {
        return [
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "XTZ",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 18,
            address: "0x0000000000000000000000000000000000000000",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "WXTZ",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 18,
            address: "0xc9B53AB2679f573e480d01e0f49e2B5CFB7a3EAb",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "USDC",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 6,
            address: "0x796Ea11Fa2dD751eD01b53C372fFDB4AAa8f00F9",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "USDT",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 6,
            address: "0x2C03058C8AFC06713be23e58D2febC8337dbfE6A",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "WETH",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 18,
            address: "0xfc24f770F94edBca6D6f885E12d4317320BcB401",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "WBTC",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 8,
            address: "0xbFc94CD2B1E55999Cfc7347a9313e88702B83d0F",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "WBNB",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 18,
            address: "0xaA40A1cc1561c584B675cbD12F1423A32E2a0d8C",
          },
        ];
      }

      return [];
    }

    const response = await fetch(`https://tokens.1inch.io/v1.1/${inchChainId}`);
    const data: TokenListResponse = await response.json();

    const popularSymbols = [
      "USDC",
      "USDT",
      "DAI",
      "WETH",
      "LINK",
      "UNI",
      "AAVE",
      "MATIC",
      "CRV",
      "COMP",
      "MKR",
      "SNX",
      "BAL",
      "YFI",
      "1INCH",
      "SUSHI",
      "REN",
      "BAND",
      "ZRX",
      "WBTC",
      "SHIB",
      "LDO",
      "ARB",
      "OP",
      "PEPE",
      "DOGE",
      "APT",
      "SUI",
      "SEI",
      "TIA",
    ];

    const tokens: TokenWithAddress[] = Object.values(data)
      .filter(
        token =>
          // Filter for popular tokens
          popularSymbols.includes(token.symbol) ||
          token.tags?.includes("popular") ||
          token.symbol === "ETH" ||
          token.symbol === "WETH"
      )
      .map(token => ({
        chainId,
        symbol: token.symbol,
        balance: "0",
        usdPrice: 0,
        totalUsdPrice: 0,
        decimals: token.decimals,
        address: token.address,
        logoURI: token.logoURI,
      }))
      .slice(0, 50);

    tokenCache.set(cacheKey, tokens);

    return tokens;
  } catch (error) {
    console.error("Error fetching popular tokens:", error);

    return [];
  }
};

export const searchTokens = async (
  query: string,
  chainId: ChainFullId
): Promise<TokenWithAddress[]> => {
  const cacheKey = `search-${chainId}-${query.toLowerCase()}`;

  if (tokenCache.has(cacheKey)) {
    return tokenCache.get(cacheKey)!;
  }

  try {
    const chainIdMap: Record<string, string> = {
      [ChainFullIds.ETHEREUM]: "1",
      [ChainFullIds.BSC]: "56",
    };

    const inchChainId = chainIdMap[chainId];

    if (!inchChainId) {
      if (chainId === ChainFullIds.ETHERLINK) {
        const etherlinkTokens = [
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "XTZ",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 18,
            address: "",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "USDC",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 6,
            address: "0x796Ea11Fa2dD751eD01b53C372fFDB4AAa8f00F9",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "USDT",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 6,
            address: "0x2C03058C8AFC06713be23e58D2febC8337dbfE6A",
          },
          {
            chainId: ChainFullIds.ETHERLINK,
            symbol: "WETH",
            balance: "0",
            usdPrice: 0,
            totalUsdPrice: 0,
            decimals: 18,
            address: "0xfc24f770F94edBca6D6f885E12d4317320BcB401",
          },
        ];

        const filteredTokens = etherlinkTokens.filter(
          token =>
            token.symbol.toLowerCase().includes(query.toLowerCase()) ||
            token.address.toLowerCase().includes(query.toLowerCase())
        );

        tokenCache.set(cacheKey, filteredTokens);

        return filteredTokens;
      }

      return [];
    }

    const response = await fetch(`https://tokens.1inch.io/v1.1/${inchChainId}`);
    const data: TokenListResponse = await response.json();

    const tokens: TokenWithAddress[] = Object.values(data)
      .filter(
        token =>
          token.symbol.toLowerCase().includes(query.toLowerCase()) ||
          token.name.toLowerCase().includes(query.toLowerCase()) ||
          token.address.toLowerCase().includes(query.toLowerCase())
      )
      .map(token => ({
        chainId,
        symbol: token.symbol,
        balance: "0",
        usdPrice: 0,
        totalUsdPrice: 0,
        decimals: token.decimals,
        address: token.address,
        logoURI: token.logoURI,
      }))
      .slice(0, 20);

    tokenCache.set(cacheKey, tokens);

    return tokens;
  } catch (error) {
    console.error("Error searching tokens:", error);

    return [];
  }
};
